import React, { useCallback, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  defaultForm,
  formDefinitionPathEntry,
  formResponsesPathEntry,
  PathEntry,
  useYDocReducer,
} from "@/hooks/useY";
import SectionEditor from "@/components/authoring/SectionEditor";
import { useQuery } from "@/hooks/useQuery";
import { formAuthoringReducer } from "@/scripts/formAuthoringReducer";
import _ from "lodash";
import { FormDefinition } from "@/types/form";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";


const FormAuthoringPage = () => {
  const { formResponseId } = useQuery();

  const formPath = useMemo<PathEntry[]>(() => {
    if (formResponseId === undefined) {
      return [formDefinitionPathEntry];
    }

    return [formResponsesPathEntry, { type: "array", value: formResponseId }, formDefinitionPathEntry];
  }, [formResponseId]);

  const [formDefinition, dispatchFormDefinition] = useYDocReducer(
    formPath,
    formAuthoringReducer,
    defaultForm
  );

  const sections = formDefinition.sections;

  const [editFieldId, setEditFieldId] = useState<string | undefined>(
    sections?.at(0)?.fields?.at(0)?.id
  );

  const importFormDefinition = useCallback(() => {
    // upload form response and set it to formResponse
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      const json = await file.text();
      const formDefinition: FormDefinition = JSON.parse(json);
      input.remove();

      // Check Structure
      if (
        !formDefinition ||
        !formDefinition.sections ||
        !formDefinition.sections.every((section) => section.fields.every((field) => field.id && field.type))
      ) {
        alert("Invalid form response file");
        return;
      }

      dispatchFormDefinition({ type: "REPLACE_FORM_DEFINITION", payload: formDefinition });
    };

    input.click();

  }, [dispatchFormDefinition]);

  const exportFormDefinition = useCallback(() => {
    // Create a json file with the content of form response
    const json = JSON.stringify(formDefinition, null, 2);

    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `form-definition.json`;
    a.click();
    a.remove();
  }, [formDefinition]);

  const handleCreateNewSection = useCallback(() => {
    dispatchFormDefinition({
      type: "ADD_SECTION",
    });
  }, [dispatchFormDefinition]);

  const branchingSections = sections.map((section) => ({
    id: section.id,
    title: section.title,
  }));

  const memoizedBranchingSections = useRef(branchingSections);

  if (!_.isEqual(memoizedBranchingSections.current, branchingSections)) {
    memoizedBranchingSections.current = branchingSections;
  }

  return (
    <Mui.Box className="p-8">
      <Mui.Box display="flex" justifyContent="space-between" alignItems="center">
        <Mui.Typography variant="h4" gutterBottom>
          Form Authoring
        </Mui.Typography>
        <Mui.Box display="flex">
          <Mui.Tooltip title="Export">
            <Mui.IconButton color="primary" onClick={exportFormDefinition}>
              <UploadIcon />
            </Mui.IconButton>
          </Mui.Tooltip>
          <Mui.Tooltip title="Import">
            <Mui.IconButton color="primary" onClick={importFormDefinition}>
              <DownloadIcon />
            </Mui.IconButton>
          </Mui.Tooltip>
        </Mui.Box>
      </Mui.Box>
      {sections.map((section) => (
        <SectionEditor
          key={section.id}
          section={section}
          editFieldId={editFieldId}
          branchingSections={memoizedBranchingSections.current}
          isTheOnlyStartingSection={
            sections.filter((section) => section.isStartingSection).length === 1 && section.isStartingSection
          }
          titleFieldId={formDefinition.titleFieldId}
          setEditFieldId={setEditFieldId}
          dispatch={dispatchFormDefinition}
        />
      ))}

      {/* New Section Button */}
      <Mui.Button
        variant="contained"
        color="primary"
        onClick={handleCreateNewSection}
        className="mt-4"
        startIcon={<AddIcon />}
      >
        Add New Section
      </Mui.Button>
    </Mui.Box>
  );
};

export default FormAuthoringPage;
